import './style/importExportDialog.scss';
import {AwesomeIcon, Link, Loader} from '@dvrd/dvr-controls';
import classNames from 'classnames';
import React, {MouseEventHandler, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {BluemateButton, BluemateCheckbox, BluemateDialog, BluemateSegmentedToggle} from '../../controls';

export type ImportExportMode = 'vat' | 'cost';

interface Props {
    onClose: VoidFunction;
    onChangeMode: (mode: ImportExportMode) => void;
    onClickExport: MouseEventHandler;
    onSelectFile: (file: File | null) => void;
    onChangeApplyRetroactive: VoidFunction;
    onSubmitImport: VoidFunction;
    onDownloadTemplate: VoidFunction;
    onDownloadErrorFile: VoidFunction;
    active: boolean;
    mode: ImportExportMode;
    exporting: boolean;
    importing: boolean;
    importFile: File | null;
    applyRetroactive: boolean;
    importError: ImportErrorState;
    title?: string;
    modesHidden?: boolean;
}

export type ImportErrorState = {kind: 'text'; text: string} | {kind: 'rows'; rows: Record<number, string>} | null;

export default function ImportExportDialog(props: Props) {
    const {t} = useTranslation();
    const {
        onClose,
        onChangeMode,
        onClickExport,
        onSelectFile,
        onChangeApplyRetroactive,
        onSubmitImport,
        onDownloadTemplate,
        onDownloadErrorFile,
        active,
        mode,
        exporting,
        importing,
        importFile,
        applyRetroactive,
        importError,
        title = t('products.importExport.title', 'Importeren & Exporteren'),
        modesHidden
    } = props;
    const [dragActive, setDragActive] = useState(false);
    const [costErrorsExpanded, setCostErrorsExpanded] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    function onToggleCostErrors() {
        setCostErrorsExpanded(!costErrorsExpanded);
    }

    function onOpenFileSelection() {
        inputRef.current?.click();
    }

    function onChangeFileInput(evt: React.ChangeEvent<HTMLInputElement>) {
        onSelectFile(evt.target.files?.[0] ?? null);
        evt.target.value = '';
    }

    function onDragOver(evt: React.DragEvent) {
        evt.preventDefault();
        setDragActive(true);
    }

    function onDragLeave() {
        setDragActive(false);
    }

    function onDrop(evt: React.DragEvent) {
        evt.preventDefault();
        setDragActive(false);
        onSelectFile(evt.dataTransfer.files?.[0] ?? null);
    }

    function renderModeToggle() {
        return (
            <div className='mode-container'>
                <label className='mode-label'>
                    {t('products.importExport.chooseLabel', 'Kies wat je wilt importeren of exporteren:')}
                </label>
                <BluemateSegmentedToggle<ImportExportMode>
                    options={[
                        {value: 'vat', label: t('products.importExport.modeVat', 'BTW')},
                        {value: 'cost', label: t('products.importExport.modeCost', 'Kosten')}
                    ]}
                    value={mode}
                    onChange={onChangeMode}
                    fullWidth
                />
            </div>
        );
    }

    //===========================
    // VAT
    //===========================
    function renderVATExportCard() {
        return (
            <div className='action-card'>
                <div className='card-title'>
                    <AwesomeIcon name='download' className='card-icon' />
                    <label>
                        {t('products.importExport.vat.exportTitle', 'Exporteren')}
                        {!modesHidden && ' (' + t('products.importExport.vat.vat', 'BTW') + ')'}
                    </label>
                </div>
                <p className='card-description'>
                    {t(
                        'products.importExport.vat.exportDescription',
                        'Download een Excel-overzicht van al je producten met hun huidige BTW-tarieven.'
                    )}
                </p>
                <BluemateButton
                    onClick={onClickExport}
                    label={t('products.importExport.vat.exportButton', 'Exporteer BTW (.xlsx)')}
                    ornaments={{element: <AwesomeIcon name='download' />}}
                    loading={exporting}
                    className='export-button'
                    secondary
                    shape='square'
                    align='center'
                />
            </div>
        );
    }

    function renderVATImportCard() {
        return (
            <div className='action-card'>
                <Loader active={importing} />
                <div className='card-title'>
                    <AwesomeIcon name='upload' className='card-icon' />
                    <label>
                        {t('products.importExport.vat.importTitle', 'Importeren')}
                        {!modesHidden && ' (' + t('products.importExport.vat.vat', 'BTW') + ')'}
                    </label>
                </div>
                <p className='card-description'>
                    {t(
                        'products.importExport.vat.importDescription',
                        'Upload een bewerkt Excel-bestand om de BTW-tarieven in bulk bij te werken.'
                    )}
                </p>
                <input type='file' ref={inputRef} className='file-input' onChange={onChangeFileInput} accept='.xlsx' />
                <div
                    className={classNames('dropzone', dragActive && 'drag-active')}
                    onClick={onOpenFileSelection}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                >
                    <AwesomeIcon name='file-excel' className='dropzone-icon' />
                    <label className='dropzone-label'>
                        {importFile?.name ??
                            t('products.importExport.dropzoneLabel', 'Klik om een .xlsx-bestand te kiezen')}
                    </label>
                    <label className='dropzone-hint'>
                        {t('products.importExport.dropzoneHint', 'Of sleep het bestand hierheen (.xlsx)')}
                    </label>
                </div>
                <label className='format-note'>
                    <Link onClick={onDownloadTemplate}>
                        {t('products.importExport.templateLink', 'Download het voorbeeldbestand')}
                    </Link>{' '}
                    — {t('products.importExport.vat.formatNote', 'formaat: EAN, BTW % per land')}
                </label>
                {renderVATImportActions()}
                {renderVATImportError()}
            </div>
        );
    }

    function renderVATImportActions() {
        if (!importFile) return null;
        return (
            <div className='import-actions'>
                <BluemateCheckbox
                    checked={applyRetroactive}
                    onCheck={onChangeApplyRetroactive}
                    label={t('products.importExport.vat.applyToExisting', 'Pas BTW ook toe op bestaande facturen')}
                />
                <BluemateButton
                    onClick={onSubmitImport}
                    label={t('products.importExport.upload', 'Uploaden')}
                    ornaments={{element: <AwesomeIcon name='upload' />}}
                    loading={importing}
                    className='upload-button'
                    shape='square'
                    align='center'
                />
            </div>
        );
    }

    function renderVATImportError() {
        if (importError?.kind !== 'text') return null;
        return (
            <div className='import-error'>
                <label className='error-title'>
                    {t('products.importExport.importDoneWithErrors', 'Import voltooid, met fouten')}
                </label>
                <p className='error-text'>{importError.text}</p>
                <Link onClick={onDownloadErrorFile}>
                    {t('products.importExport.vat.downloadErrorFile', 'Download import-fouten.xlsx')}
                </Link>
            </div>
        );
    }

    //===========================
    // Product Costs
    //===========================
    function renderCostsSubtitle() {
        return (
            <div className='subtitle-container'>
                <AwesomeIcon name='info-circle' className='info-icon' />
                <label className='subtitle'>
                    {t(
                        'products.importExport.costs.subtitle',
                        'Je kunt productkosten ook per product aanpassen door in de tabel op de cel met productkosten te klikken.'
                    )}
                </label>
            </div>
        );
    }

    function renderCostExportCard() {
        return (
            <div className='action-card'>
                <div className='card-title'>
                    <AwesomeIcon name='download' className='card-icon' />
                    <label>
                        {t('products.importExport.costs.exportTitle', 'Exporteren')}
                        {!modesHidden && ' (' + t('products.importExport.costs.productCosts', 'Productkosten') + ')'}
                    </label>
                </div>
                <p className='card-description'>
                    {t(
                        'products.importExport.costs.exportDescription',
                        'Download een Excel-overzicht van al je ingestelde productkosten.'
                    )}
                </p>
                <BluemateButton
                    onClick={onClickExport}
                    label={t('products.importExport.costs.exportButton', 'Exporteer productkosten (.xlsx)')}
                    ornaments={{element: <AwesomeIcon name='download' />}}
                    loading={exporting}
                    className='export-button'
                    secondary
                    shape='square'
                    align='center'
                />
            </div>
        );
    }

    function renderCostImportCard() {
        return (
            <div className='action-card'>
                <Loader active={importing} />
                <div className='card-title'>
                    <AwesomeIcon name='upload' className='card-icon' />
                    <label>
                        {t('products.importExport.costs.importTitle', 'Importeren')}
                        {!modesHidden && ' (' + t('products.importExport.costs.productCosts', 'Productkosten') + ')'}
                    </label>
                </div>
                <p className='card-description'>
                    {t(
                        'products.importExport.costs.importDescription',
                        'Upload een Excel-bestand om de productkosten in bulk bij te werken.'
                    )}
                </p>
                <input type='file' ref={inputRef} className='file-input' onChange={onChangeFileInput} accept='.xlsx' />
                <div
                    className={classNames('dropzone', dragActive && 'drag-active')}
                    onClick={onOpenFileSelection}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                >
                    <AwesomeIcon name='file-excel' className='dropzone-icon' />
                    <label className='dropzone-label'>
                        {importFile?.name ??
                            t('products.importExport.dropzoneLabel', 'Klik om een .xlsx-bestand te kiezen')}
                    </label>
                    <label className='dropzone-hint'>
                        {t('products.importExport.dropzoneHint', 'Of sleep het bestand hierheen (.xlsx)')}
                    </label>
                </div>
                <label className='format-note'>
                    <Link onClick={onDownloadTemplate}>
                        {t('products.importExport.templateLink', 'Download het voorbeeldbestand')}
                    </Link>{' '}
                    —{' '}
                    {t(
                        'products.importExport.costs.formatNote',
                        'formaat: EAN, Startdatum, Kostprijs, Vanaf aantal artikelen, Fulfilment kosten, Verzendkosten, Retourlabel kosten, Overige retour kosten, Beschrijving overige retour kosten, Overige kosten, Beschrijving overige kosten'
                    )}
                </label>
                {renderCostImportActions()}
                {renderCostImportError()}
            </div>
        );
    }

    function renderCostImportActions() {
        if (!importFile) return null;
        return (
            <div className='import-actions'>
                <div />
                <BluemateButton
                    onClick={onSubmitImport}
                    label={t('products.importExport.upload', 'Uploaden')}
                    ornaments={{element: <AwesomeIcon name='upload' />}}
                    loading={importing}
                    className='upload-button'
                    shape='square'
                    align='center'
                />
            </div>
        );
    }

    function renderCostImportError() {
        if (importError?.kind !== 'rows') return null;
        return (
            <div className='import-error'>
                <label className='error-title'>
                    {t('products.importExport.importDoneWithErrors', 'Import voltooid, met fouten')}
                </label>
                <div className={classNames('cost-import-errors-container', costErrorsExpanded && 'expanded')}>
                    <div className='cost-import-errors'>
                        {Object.values(importError.rows).map((error: string, index: number) => (
                            <span className='cost-import-error' key={index}>
                                {error}
                            </span>
                        ))}
                    </div>
                    <div className='cost-errors-toggle'>
                        <AwesomeIcon name='chevron-down' className='toggle-icon' onClick={onToggleCostErrors} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <BluemateDialog
            active={active}
            onClose={onClose}
            title={title}
            cancelLabel={t('products.importExport.close', 'Sluiten')}
            cancelVariant='tertiary'
            loading={exporting || importing}
            className='products-import-export-dialog'
        >
            {mode === 'cost' && renderCostsSubtitle()}
            {!modesHidden && renderModeToggle()}
            {mode === 'vat' && (
                <>
                    {renderVATExportCard()}
                    {renderVATImportCard()}
                </>
            )}
            {mode === 'cost' && (
                <>
                    {renderCostExportCard()}
                    {renderCostImportCard()}
                </>
            )}
        </BluemateDialog>
    );
}
